"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  angularAcceleration,
  type InertiaError,
  momentOfInertia,
  SHAPES,
  type Shape,
} from "@/lib/tool-kit/physics-mechanics/torque";
import {
  ANGULAR_ACCELERATION,
  fromBase,
  LENGTH,
  MASS,
  MOMENT_OF_INERTIA,
  TORQUE,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

type Dimension = "R" | "L" | "a" | "b" | "r";

export interface InertiaField {
  value: string;
  unit: string;
}

export interface InertiaState {
  shape: Shape;
  mass: InertiaField;
  sizes: Record<Dimension, InertiaField>;
  offset: InertiaField;
  torque: InertiaField;
}

export interface InertiaStrings {
  shapeLabel: string;
  shapeDisk: string;
  shapeHoop: string;
  shapeSolidSphere: string;
  shapeShell: string;
  shapeRodCentre: string;
  shapeRodEnd: string;
  shapePlate: string;
  shapePoint: string;
  inertiaMass: string;
  dimensionR: string;
  dimensionL: string;
  dimensionA: string;
  dimensionB: string;
  dimensionPoint: string;
  offset: string;
  offsetHint: string;
  torqueOptional: string;
  torqueHint: string;
  unitFor: string;
  inertia: string;
  centreInertia: string;
  angularAcceleration: string;
  workingHeading: string;
  stepFormula: string;
  stepShift: string;
  stepAlpha: string;
  copy: string;
  copied: string;
  copyText: string;
  inertiaNeed: string;
  inertiaUnreadable: string;
  inertiaErrors: Record<InertiaError, string>;
}

const DIMENSION_KEYS: Record<Dimension, keyof InertiaStrings> = {
  R: "dimensionR",
  L: "dimensionL",
  a: "dimensionA",
  b: "dimensionB",
  r: "dimensionPoint",
};

const SHAPE_LABELS: Record<Shape, keyof InertiaStrings> = {
  disk: "shapeDisk",
  hoop: "shapeHoop",
  solidSphere: "shapeSolidSphere",
  shell: "shapeShell",
  rodCentre: "shapeRodCentre",
  rodEnd: "shapeRodEnd",
  plate: "shapePlate",
  point: "shapePoint",
};

const SHAPE_ORDER: readonly Shape[] = [
  "disk",
  "hoop",
  "solidSphere",
  "shell",
  "rodCentre",
  "rodEnd",
  "plate",
  "point",
];

export function InertiaInputs({
  state,
  onState,
  strings,
}: {
  state: InertiaState;
  onState: (state: InertiaState) => void;
  strings: InertiaStrings;
}) {
  const info = SHAPES[state.shape];
  const patchSize = (key: Dimension, change: Partial<InertiaField>) =>
    onState({
      ...state,
      sizes: { ...state.sizes, [key]: { ...state.sizes[key], ...change } },
    });
  return (
    <>
      <SelectField
        id="tq-shape"
        label={strings.shapeLabel}
        onChange={(shape) => onState({ ...state, shape })}
        options={SHAPE_ORDER.map((shape) => ({
          value: shape,
          label: String(strings[SHAPE_LABELS[shape]]),
        }))}
        value={state.shape}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="tq-mass"
          label={strings.inertiaMass}
          onUnit={(unit) =>
            onState({ ...state, mass: { ...state.mass, unit } })
          }
          onValue={(value) =>
            onState({ ...state, mass: { ...state.mass, value } })
          }
          unit={state.mass.unit}
          unitLabel={t(strings.unitFor, { name: strings.inertiaMass })}
          units={unitOptions(MASS)}
          value={state.mass.value}
        />
        {info.dimensions.map((key) => {
          const label = String(strings[DIMENSION_KEYS[key]]);
          return (
            <QuantityField
              id={`tq-size-${key}`}
              key={key}
              label={label}
              onUnit={(unit) => patchSize(key, { unit })}
              onValue={(value) => patchSize(key, { value })}
              unit={state.sizes[key].unit}
              unitLabel={t(strings.unitFor, { name: label })}
              units={unitOptions(LENGTH)}
              value={state.sizes[key].value}
            />
          );
        })}
        {info.centred ? (
          <QuantityField
            hint={strings.offsetHint}
            id="tq-offset"
            label={strings.offset}
            onUnit={(unit) =>
              onState({ ...state, offset: { ...state.offset, unit } })
            }
            onValue={(value) =>
              onState({ ...state, offset: { ...state.offset, value } })
            }
            unit={state.offset.unit}
            unitLabel={t(strings.unitFor, { name: strings.offset })}
            units={unitOptions(LENGTH)}
            value={state.offset.value}
          />
        ) : null}
        <QuantityField
          hint={strings.torqueHint}
          id="tq-torque"
          label={strings.torqueOptional}
          onUnit={(unit) =>
            onState({ ...state, torque: { ...state.torque, unit } })
          }
          onValue={(value) =>
            onState({ ...state, torque: { ...state.torque, value } })
          }
          unit={state.torque.unit}
          unitLabel={t(strings.unitFor, { name: strings.torqueOptional })}
          units={unitOptions(TORQUE)}
          value={state.torque.value}
        />
      </div>
    </>
  );
}

function readField(field: InertiaField): number | null | "empty" {
  return field.value.trim() === "" ? "empty" : parseQuantity(field.value);
}

export function InertiaResults({
  state,
  strings,
  fmt,
  sig,
}: {
  state: InertiaState;
  strings: InertiaStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const info = SHAPES[state.shape];
  const mass = readField(state.mass);
  if (mass === "empty") {
    return <Notice>{strings.inertiaNeed}</Notice>;
  }
  const sizes: Partial<Record<Dimension, number>> = {};
  for (const key of info.dimensions) {
    const parsed = readField(state.sizes[key]);
    if (parsed === "empty") {
      return <Notice>{strings.inertiaNeed}</Notice>;
    }
    if (parsed === null) {
      return <ErrorNotice>{strings.inertiaUnreadable}</ErrorNotice>;
    }
    sizes[key] = toBase(parsed, LENGTH, state.sizes[key].unit);
  }
  const offset = info.centred ? readField(state.offset) : "empty";
  const torque = readField(state.torque);
  if (mass === null || offset === null || torque === null) {
    return <ErrorNotice>{strings.inertiaUnreadable}</ErrorNotice>;
  }
  const massSi = toBase(mass, MASS, state.mass.unit);
  const offsetSi =
    offset === "empty" ? 0 : toBase(offset, LENGTH, state.offset.unit);
  const result = momentOfInertia(state.shape, massSi, sizes, offsetSi);
  if ("error" in result) {
    return <ErrorNotice>{strings.inertiaErrors[result.error]}</ErrorNotice>;
  }
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const inertiaUnit = "kg·m²";
  const steps = [
    t(strings.stepFormula, {
      formula: info.formula,
      mass: p(massSi),
      sizes: info.dimensions
        .map((key) => `${key} = ${p(sizes[key] ?? 0)} m`)
        .join(", "),
      value: r(result.centreInertia),
    }),
  ];
  if (offsetSi > 0) {
    steps.push(
      t(strings.stepShift, {
        centre: r(result.centreInertia),
        mass: p(massSi),
        offset: p(offsetSi),
        value: r(result.inertia),
      })
    );
  }
  let alpha: number | null = null;
  if (torque !== "empty") {
    alpha = angularAcceleration(
      toBase(torque, TORQUE, state.torque.unit),
      result.inertia
    );
    steps.push(
      t(strings.stepAlpha, {
        torque: p(toBase(torque, TORQUE, state.torque.unit)),
        inertia: r(result.inertia),
        alpha: r(alpha),
      })
    );
  }
  const shownInertia = withUnit(
    formatSig(fmt, fromBase(result.inertia, MOMENT_OF_INERTIA, "kgm2"), sig),
    inertiaUnit
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.inertia} value={shownInertia} />
        {offsetSi > 0 ? (
          <StatCard
            label={strings.centreInertia}
            value={withUnit(r(result.centreInertia), inertiaUnit)}
          />
        ) : null}
        {alpha === null ? null : (
          <StatCard
            emphasis
            label={strings.angularAcceleration}
            value={withUnit(
              r(fromBase(alpha, ANGULAR_ACCELERATION, "radps2")),
              unitSymbol(ANGULAR_ACCELERATION, "radps2")
            )}
          />
        )}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.copyText, { inertia: shownInertia })}
      />
      <Working heading={strings.workingHeading} steps={steps} />
    </>
  );
}
